<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { convertSecondsToTimeUnit } from '@chatwoot/utils';
import { picoSearch } from '@scmmishra/pico-search';
import { Information16 } from '@carbon/icons-vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import AddSLA from './AddSLA.vue';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import WootLabel from 'dashboard/components-next/label/Label.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import RowMenu from 'dashboard/components-next/polaris/cards/RowMenu.vue';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'dashboard/components-next/polaris/ui/tooltip';
import { useSettingsRowMenu } from '../components/useSettingsRowMenu';

// D-S4 / X-5 (phase 9.2): no plan lock here. The page no longer checks the `sla` flag: without it the menu item
// (and the Home card) is gone, and the upgrade card (SLAPaywallEnterprise) left with it.
const store = useStore();
const { t } = useI18n();
const { rowMenuItems, rowMenuLabel } = useSettingsRowMenu();

const records = useMapGetter('sla/getSLA');
const uiFlags = useMapGetter('sla/getUIFlags');

const showAddPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedResponse = ref({});
const searchQuery = ref('');

const deleteMessage = computed(() => ` ${selectedResponse.value.name}`);

const tableHeaders = computed(() => [
  t('SLA.LIST.TABLE_HEADER.SLA'),
  t('SLA.LIST.TABLE_HEADER.BUSINESS_HOURS'),
  t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.FRT'),
  t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.NRT'),
  t('SLA.LIST.RESPONSE_TYPES.SHORT_HAND.RT'),
  t('INTEGRATION_APPS.LIST.ACTIONS'),
]);

// The three time columns keep their full name in a tooltip, as before.
const timeHeaders = computed(() => ({
  2: t('SLA.LIST.RESPONSE_TYPES.FRT'),
  3: t('SLA.LIST.RESPONSE_TYPES.NRT'),
  4: t('SLA.LIST.RESPONSE_TYPES.RT'),
}));

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, ['name', 'description']);
});

const openAddPopup = () => {
  showAddPopup.value = true;
};
const openDeletePopup = sla => {
  showDeleteConfirmationPopup.value = true;
  selectedResponse.value = sla;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteSla = async id => {
  try {
    await store.dispatch('sla/delete', id);
    useAlert(t('SLA.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('SLA.DELETE.API.ERROR_MESSAGE'));
  }
};

const confirmDeletion = () => {
  closeDeletePopup();
  deleteSla(selectedResponse.value.id);
};

const displayTime = threshold => {
  const { time, unit } = convertSecondsToTimeUnit(threshold, {
    minute: 'm',
    hour: 'h',
    day: 'd',
  });
  if (!time) return '-';
  return `${time}${unit}`;
};

onMounted(() => {
  store.dispatch('sla/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('SLA.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('SLA.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('SLA.HEADER')"
        :description="$t('SLA.DESCRIPTION')"
        :link-text="$t('SLA.LEARN_MORE')"
        :search-placeholder="$t('SLA.SEARCH_PLACEHOLDER')"
        feature-name="sla"
      >
        <template v-if="records?.length" #count>
          {{ $t('SLA.COUNT', { n: records.length }) }}
        </template>
        <template #actions>
          <NextButton
            :label="$t('SLA.ADD_ACTION')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #drawer>
      <AddSLA v-model:open="showAddPopup" />
    </template>
    <template #emptyActions>
      <NextButton
        :label="$t('SLA.ADD_ACTION')"
        size="sm"
        outline
        @click="openAddPopup"
      />
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="$t('SLA.SEARCH.NO_RESULTS')"
        pin-last-column
        :clearable="!!searchQuery"
        @clear="searchQuery = ''"
      >
        <template
          v-for="(fullName, index) in timeHeaders"
          :key="index"
          #[`header-${index}`]
        >
          <span class="inline-flex items-center gap-1">
            {{ tableHeaders[index] }}
            <Tooltip>
              <TooltipTrigger as-child>
                <button
                  type="button"
                  class="inline-flex size-4 items-center justify-center rounded-polaris-sm p-0 text-polaris-muted-foreground"
                  :aria-label="fullName"
                >
                  <Information16 class="size-3.5" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{{ fullName }}</TooltipContent>
            </Tooltip>
          </span>
        </template>
        <template #row="{ items }">
          <BaseTableRow v-for="sla in items" :key="sla.id" :item="sla">
            <BaseTableCell wrap>
              <span class="block">{{ sla.name }}</span>
              <span
                v-if="sla.description"
                class="mt-0.5 line-clamp-2 font-normal text-polaris-muted-foreground"
              >
                {{ sla.description }}
              </span>
            </BaseTableCell>
            <BaseTableCell>
              <WootLabel
                :label="
                  sla.only_during_business_hours
                    ? $t('SLA.LIST.BUSINESS_HOURS_ON')
                    : $t('SLA.LIST.BUSINESS_HOURS_OFF')
                "
                :color="sla.only_during_business_hours ? 'teal' : 'slate'"
                compact
              >
                <template #icon>
                  <Icon
                    :icon="
                      sla.only_during_business_hours
                        ? 'i-lucide-alarm-clock-check'
                        : 'i-lucide-alarm-clock-off'
                    "
                    class="size-3.5"
                  />
                </template>
              </WootLabel>
            </BaseTableCell>
            <BaseTableCell>
              {{ displayTime(sla.first_response_time_threshold) }}
            </BaseTableCell>
            <BaseTableCell>
              {{ displayTime(sla.next_response_time_threshold) }}
            </BaseTableCell>
            <BaseTableCell>
              {{ displayTime(sla.resolution_time_threshold) }}
            </BaseTableCell>
            <BaseTableCell actions>
              <RowMenu
                :label="rowMenuLabel(sla.name)"
                :items="rowMenuItems(['delete'])"
                @action="openDeletePopup(sla)"
              />
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('SLA.DELETE.CONFIRM.TITLE')"
      :message="$t('SLA.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('SLA.DELETE.CONFIRM.YES')"
      :reject-text="$t('SLA.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
